import AnalyticFeedsIcon from '@apps-common/ui-icons/AnalyticFeeds.jsx';
import BasemapViewerIcon from '@apps-common/ui-icons/BasemapViewer.jsx';
import DevCenterIcon from '@apps-common/ui-icons/DevCenter.jsx';
import ExplorerIcon from '@apps-common/ui-icons/Explorer.jsx';
import PropTypes from 'prop-types';
import React, {useMemo} from 'react';
import SentinelHubIcon from '@apps-common/ui-icons/Sentinelhub.jsx';
import SinergiseIcon from '@apps-common/ui-icons/Sinergise.jsx';
import StoriesIcon from '@apps-common/ui-icons/Stories.jsx';
import TaskingIcon from '@apps-common/ui-icons/TaskingDashboard.jsx';
import {
  Apps as AppsIcon,
  AddLocationAltOutlined as FeaturesManagerIcon,
} from '@mui/icons-material';
import {MenuItem} from '@mui/material';

import ListItemIcon from './ListItemIcon.jsx';
import NavMenuButton from './NavMenuButton.jsx';

const APPS = [
  {
    title: 'Explorer',
    subtitle: 'Explore Planet data',
    href: 'https://www.planet.com/explorer',
    icon: <ExplorerIcon />,
    key: 'explorer',
  },
  {
    title: 'Stories',
    subtitle: 'Share timelapse comparisons',
    href: 'https://www.planet.com/stories',
    icon: <StoriesIcon />,
    key: 'stories',
  },
  {
    title: 'Basemaps Viewer',
    subtitle: 'Recent and ready to integrate basemaps',
    href: 'https://www.planet.com/basemaps',
    icon: <BasemapViewerIcon />,
    key: 'basemapsViewer',
  },
  {
    title: 'Tasking Dashboard',
    subtitle: 'High-res imagery upon request',
    href: 'https://www.planet.com/tasking',
    icon: <TaskingIcon />,
    key: 'tasking',
  },
  {
    title: 'Features Manager',
    subtitle: 'Manage your feature collections',
    href: 'https://www.planet.com/features',
    icon: <FeaturesManagerIcon />,
    key: 'featuresManager',
  },
  {
    title: 'Analytic Feeds',
    subtitle: '',
    href: 'https://www.planet.com/feeds',
    icon: <AnalyticFeedsIcon />,
    key: 'feedsViewer',
  },
  {
    title: 'Developer Center',
    subtitle: 'Plug into your Planet',
    href: 'https://developers.planet.com/',
    icon: <DevCenterIcon />,
    key: 'devCenter',
  },
];

const SENTINEL_HUB_APPS = [
  {
    title: 'Sentinel Hub Dashboard',
    subtitle:
      'Manage your Sentinel Hub account, collections, and monitor usage',
    href: 'https://apps.sentinel-hub.com/dashboard/?kc_idp_hint=planet-account-link',
    icon: <SentinelHubIcon />,
    key: 'sentinelhub',
  },
  {
    title: 'EO Browser',
    subtitle: 'Browse and compare images',
    href: 'https://apps.sentinel-hub.com/eo-browser/?kc_idp_hint=planet-account-link',
    icon: <SinergiseIcon />,
    key: 'eobrowser',
  },
];

/**
 * Menu with links to Planet web applications. Used in `NavBar` component, but can be imported as a standalone component as well.
 */
const AppsMenu = ({exclude, includeSentinelHubApps}) => {
  // Exclude app(s) from menu
  const filteredApps = useMemo(() => {
    let apps = [];
    if (Array.isArray(exclude)) {
      apps = APPS.filter(app => exclude.indexOf(app.key) < 0);
    } else {
      apps = APPS.filter(app => app.key !== exclude);
    }

    return includeSentinelHubApps ? [...apps, ...SENTINEL_HUB_APPS] : apps;
  }, [exclude, includeSentinelHubApps]);

  return (
    <NavMenuButton
      title="Applications Menu"
      label={<AppsIcon fontSize="small" />}
      data-qe="application-menu-button"
      aria-label="Planet Applications"
    >
      {filteredApps.map(app => (
        <MenuItem
          key={app.key}
          title={app.subtitle}
          component="a"
          target="_blank"
          rel="noreferrer noopener"
          href={app.href}
          data-qe={`${app.key}-menu-item`}
        >
          <ListItemIcon sx={{color: 'text.primary'}}>{app.icon}</ListItemIcon>{' '}
          {app.title}
        </MenuItem>
      ))}
    </NavMenuButton>
  );
};

AppsMenu.propTypes = {
  /**
   * Exclude one or more entries from the list of apps
   */
  exclude: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  /**
   * Include the Sentinel Hub App Links
   */
  includeSentinelHubApps: PropTypes.bool,
};

export default AppsMenu;
